import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { MenuItem, Order, TableInfo, MenuCategory } from '../types';
import { apiService } from '../services/apiService';
import { useOrg } from './OrgContext';
import { useDebounce } from '../hooks/useDebounce';

interface DataContextType {
  // Menu
  menuItems: MenuItem[];
  loadingMenu: boolean;
  createMenuItem: (item: Partial<MenuItem>) => Promise<void>;
  updateMenuItem: (id: number, updates: Partial<MenuItem>) => Promise<void>;
  deleteMenuItem: (id: number) => Promise<void>;
  refreshMenu: () => Promise<void>;

  // Categories
  menuCategories: MenuCategory[];
  loadingCategories: boolean;
  createMenuCategory: (category: Pick<MenuCategory, 'name' | 'description' | 'displayOrder' | 'isActive'>) => Promise<void>;
  updateMenuCategory: (id: number, updates: Partial<MenuCategory>) => Promise<void>;
  deleteMenuCategory: (id: number) => Promise<void>;
  refreshCategories: () => Promise<void>;

  // Orders
  orders: Order[];
  loadingOrders: boolean;
  createOrder: (order: Omit<Order, 'id' | 'timestamp' | 'shopId'>) => Promise<Order>;
  updateOrderStatus: (id: string, status: Order['status']) => Promise<void>;
  deleteOrder: (id: string) => Promise<void>;
  refreshOrders: () => Promise<void>;
  refreshActiveOrders: () => Promise<void>;

  // Order Limits
  orderLimits: {
    ordersInLast24Hours: number;
    maxOrdersPerDay: number;
    canCreateMore: boolean;
    remainingOrders: number;
  } | null;
  loadingOrderLimits: boolean;
  refreshOrderLimits: (forceRefresh?: boolean) => Promise<void>;

  // Tables
  tables: TableInfo[];
  loadingTables: boolean;
  createTable: (table: Omit<TableInfo, 'id'>) => Promise<void>;
  updateTable: (id: string, updates: Partial<TableInfo>) => Promise<void>;
  deleteTable: (id: string) => Promise<void>;
  refreshTables: () => Promise<void>;

  // Utility
  clearAllData: () => Promise<void>;
  loadPublicData: () => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const useDataContext = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useDataContext must be used within a DataProvider');
  }
  return context;
};

interface DataProviderProps {
  children: ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const { currentOrganization } = useOrg();
  
  // Menu state
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [loadingMenu, setLoadingMenu] = useState(false);

  // Categories state
  const [menuCategories, setMenuCategories] = useState<MenuCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Orders state
  const [orders, setOrders] = useState<Order[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  // Order Limits state with caching
  const [orderLimits, setOrderLimits] = useState<{
    ordersInLast24Hours: number;
    maxOrdersPerDay: number;
    canCreateMore: boolean;
    remainingOrders: number;
  } | null>(null);
  const [loadingOrderLimits, setLoadingOrderLimits] = useState(false);
  const [orderLimitsCache, setOrderLimitsCache] = useState<{
    data: any;
    timestamp: number;
  } | null>(null);

  // Tables state
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [loadingTables, setLoadingTables] = useState(false);

  // Menu operations
  const refreshMenu = React.useCallback(async () => {
    if (!currentOrganization) return;
    
    setLoadingMenu(true);
    try {
      const response = await apiService.getMenuItems(currentOrganization.urlName);
      if (response.success && response.data?.items) {
        setMenuItems(response.data.items);
      }
    } catch (error) {
      console.error('Failed to load menu items:', error);
    } finally {
      setLoadingMenu(false);
    }
  }, [currentOrganization]);

  const createMenuItem = async (item: Partial<MenuItem>) => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }
    
    try {
      const response = await apiService.createMenuItem(currentOrganization.urlName, item as Omit<MenuItem, 'id'>);
      if (response.success && response.data?.item) {
        setMenuItems(prev => [...prev, response.data!.item]);
      } else {
        throw new Error(response.error || 'Failed to create menu item');
      }
    } catch (error) {
      console.error('Failed to create menu item:', error);
      throw error;
    }
  };

  const updateMenuItem = async (id: number, updates: Partial<MenuItem>) => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }
    
    try {
      const response = await apiService.updateMenuItem(currentOrganization.urlName, id, updates);
      if (response.success && response.data?.item) {
        setMenuItems(prev => prev.map(item => 
          item.id === id ? response.data!.item : item
        ));
      } else {
        throw new Error(response.error || 'Failed to update menu item');
      }
    } catch (error) {
      console.error('Failed to update menu item:', error);
      throw error;
    }
  };

  const deleteMenuItem = async (id: number) => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }
    
    try {
      const response = await apiService.deleteMenuItem(currentOrganization.urlName, id);
      if (response.success) {
        setMenuItems(prev => prev.filter(item => item.id !== id));
      } else {
        throw new Error(response.error || 'Failed to delete menu item');
      }
    } catch (error) {
      console.error('Failed to delete menu item:', error);
      throw error;
    }
  };

  // Categories operations
  const refreshCategories = React.useCallback(async () => {
    if (!currentOrganization) return;
    
    setLoadingCategories(true);
    try {
      const response = await apiService.getMenuCategories(currentOrganization.urlName);
      if (response.success && response.data?.categories) {
        setMenuCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Failed to load menu categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  }, [currentOrganization]);

  const createMenuCategory = async (category: Pick<MenuCategory, 'name' | 'description' | 'displayOrder' | 'isActive'>) => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }
    
    try {
      const response = await apiService.createMenuCategory(currentOrganization.urlName, category);
      if (response.success && response.data?.category) {
        setMenuCategories(prev => [...prev, response.data!.category]);
      } else {
        throw new Error(response.error || 'Failed to create menu category');
      }
    } catch (error) {
      console.error('Failed to create menu category:', error);
      throw error;
    }
  };

  const updateMenuCategory = async (id: number, updates: Partial<MenuCategory>) => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }
    
    try {
      const response = await apiService.updateMenuCategory(currentOrganization.urlName, id, updates);
      if (response.success && response.data?.category) {
        setMenuCategories(prev => prev.map(category => 
          category.id === id ? response.data!.category : category
        ));
      } else {
        throw new Error(response.error || 'Failed to update menu category');
      }
    } catch (error) {
      console.error('Failed to update menu category:', error);
      throw error;
    }
  };

  const deleteMenuCategory = async (id: number) => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }
    
    try {
      const response = await apiService.deleteMenuCategory(currentOrganization.urlName, id);
      if (response.success) {
        setMenuCategories(prev => prev.filter(category => category.id !== id));
      } else {
        throw new Error(response.error || 'Failed to delete menu category');
      }
    } catch (error) {
      console.error('Failed to delete menu category:', error);
      throw error;
    }
  };

  // Orders operations
  const refreshOrders = React.useCallback(async () => {
    if (!currentOrganization) return;
    
    setLoadingOrders(true);
    try {
      const response = await apiService.getOrders();
      if (response.success && response.data) {
        setOrders(response.data);
      } else {
        console.error('Failed to load orders:', response.error);
      }
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  }, [currentOrganization]);

  const refreshActiveOrders = React.useCallback(async () => {
    if (!currentOrganization) return;
    
    setLoadingOrders(true);
    try {
      const response = await apiService.getActiveOrders();
      if (response.success && response.data) {
        setOrders(response.data);
      } else {
        console.error('Failed to load active orders:', response.error);
      }
    } catch (error) {
      console.error('Failed to load active orders:', error);
    } finally {
      setLoadingOrders(false);
    }
  }, [currentOrganization]);

  const refreshOrderLimits = React.useCallback(async (forceRefresh: boolean = false) => {
    if (!currentOrganization) return;
    
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
    const now = Date.now();
    
    // Check if we have cached data and it's still valid
    if (!forceRefresh && orderLimitsCache && (now - orderLimitsCache.timestamp) < CACHE_DURATION) {
      setOrderLimits(orderLimitsCache.data);
      return;
    }
    
    setLoadingOrderLimits(true);
    try {
      const response = await apiService.getOrderLimits();
      if (response.success && response.data) {
        setOrderLimits(response.data);
        // Cache the data with current timestamp
        setOrderLimitsCache({
          data: response.data,
          timestamp: now
        });
      } else {
        console.error('Failed to load order limits:', response.error);
      }
    } catch (error) {
      console.error('Failed to load order limits:', error);
    } finally {
      setLoadingOrderLimits(false);
    }
  }, [currentOrganization, orderLimitsCache]);

  const createOrder = async (order: Omit<Order, 'id' | 'timestamp' | 'shopId'>) => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }
    
    // Ensure API service has the current organization
    apiService.setCurrentOrganization(currentOrganization);
    
    try {
      const response = await apiService.createOrder(order);
      if (!response.success) {
        throw new Error(response.error || 'Failed to create order');
      }
      
      const newOrder = response.data!;
      setOrders(prev => [...prev, newOrder]);
      return newOrder;
    } catch (error) {
      console.error('Failed to create order:', error);
      throw error;
    }
  };

  const updateOrderStatus = async (id: string, status: Order['status']) => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }
    
    // Ensure API service has the current organization
    apiService.setCurrentOrganization(currentOrganization);
    
    try {
      const response = await apiService.updateOrderStatus(id, status);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update order status');
      }
      
      const updatedOrder = response.data!;
      setOrders(prev => prev.map(order => 
        order.id === id ? updatedOrder : order
      ));
    } catch (error) {
      console.error('Failed to update order status:', error);
      throw error;
    }
  };

  const deleteOrder = async (id: string) => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }
    
    // Ensure API service has the current organization
    apiService.setCurrentOrganization(currentOrganization);
    
    try {
      const response = await apiService.deleteOrder(id);
      if (!response.success) {
        throw new Error(response.error || 'Failed to delete order');
      }
      setOrders(prev => prev.filter(order => order.id !== id));
    } catch (error) {
      console.error('Failed to delete order:', error);
      throw error;
    }
  };

  // Tables operations
  const refreshTables = React.useCallback(async () => {
    if (!currentOrganization) return;
    
    setLoadingTables(true);
    try {
      const response = await apiService.getTables(currentOrganization.urlName);
      if (response.success && response.data?.tables) {
        setTables(response.data.tables);
      } else {
        console.error('Failed to load tables:', response.error);
      }
    } catch (error) {
      console.error('Failed to load tables:', error);
    } finally {
      setLoadingTables(false);
    }
  }, [currentOrganization]);

  const createTable = async (table: Pick<TableInfo, 'name' | 'description' | 'displayOrder' | 'isActive'>) => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }
    
    try {
      const response = await apiService.createTable(currentOrganization.urlName, table);
      if (response.success && response.data?.table) {
        setTables(prev => [...prev, response.data!.table]);
      } else {
        throw new Error(response.error || 'Failed to create table');
      }
    } catch (error) {
      console.error('Failed to create table:', error);
      throw error;
    }
  };

  const updateTable = async (id: string, updates: Partial<TableInfo>) => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }
    
    try {
      const response = await apiService.updateTable(currentOrganization.urlName, id, updates);
      if (response.success && response.data?.table) {
        setTables(prev => prev.map(table => 
          table.id === id ? response.data!.table : table
        ));
      } else {
        throw new Error(response.error || 'Failed to update table');
      }
    } catch (error) {
      console.error('Failed to update table:', error);
      throw error;
    }
  };

  const deleteTable = async (id: string) => {
    if (!currentOrganization) {
      throw new Error('No organization selected');
    }
    
    try {
      const response = await apiService.deleteTable(currentOrganization.urlName, id);
      if (response.success) {
        setTables(prev => prev.filter(table => table.id !== id));
      } else {
        throw new Error(response.error || 'Failed to delete table');
      }
    } catch (error) {
      console.error('Failed to delete table:', error);
      throw error;
    }
  };

  // Utility
  const clearAllData = async () => {
    try {
      await apiService.clearAllData();
      await Promise.all([refreshMenu(), refreshOrders(), refreshTables()]);
    } catch (error) {
      console.error('Failed to clear data:', error);
      throw error;
    }
  };

  // Create debounced versions of the refresh functions
  const debouncedRefreshMenu = useDebounce(refreshMenu, 1000);
  const debouncedRefreshCategories = useDebounce(refreshCategories, 1000);
  const debouncedRefreshOrders = useDebounce(refreshOrders, 1000);
  const debouncedRefreshTables = useDebounce(refreshTables, 1000);

  // Sync current organization with API service
  useEffect(() => {
    apiService.setCurrentOrganization(currentOrganization);
  }, [currentOrganization]);

  // Load initial data when organization changes
  useEffect(() => {
    if (currentOrganization) {
      // Add a small delay to ensure organization is properly set
      const timer = setTimeout(() => {
        const loadInitialData = async () => {
          try {
            await Promise.all([
              debouncedRefreshMenu(), 
              debouncedRefreshCategories(), 
              debouncedRefreshOrders(), 
              debouncedRefreshTables(),
              refreshOrderLimits()
            ]);
          } catch (error) {
            console.error('Failed to load initial data:', error);
          }
        };
        loadInitialData();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [currentOrganization, debouncedRefreshMenu, debouncedRefreshCategories, debouncedRefreshOrders, debouncedRefreshTables]);

  // Add a function to load public data (for QR code ordering)
  const loadPublicData = React.useCallback(async () => {
    if (!currentOrganization) return;
    
    try {
      // Load public menu and active tables
      const [menuResponse, tablesResponse] = await Promise.all([
        apiService.getPublicMenu(currentOrganization.urlName),
        apiService.getActiveTables(currentOrganization.urlName)
      ]);
      
      if (menuResponse.success && menuResponse.data?.items) {
        setMenuItems(menuResponse.data.items);
      }
      
      if (tablesResponse.success && tablesResponse.data?.tables) {
        setTables(tablesResponse.data.tables);
      }
    } catch (error) {
      console.error('Failed to load public data:', error);
    }
  }, [currentOrganization]);

  const value: DataContextType = {
    // Menu
    menuItems,
    loadingMenu,
    createMenuItem,
    updateMenuItem,
    deleteMenuItem,
    refreshMenu,

    // Categories
    menuCategories,
    loadingCategories,
    createMenuCategory,
    updateMenuCategory,
    deleteMenuCategory,
    refreshCategories,

    // Orders
    orders,
    loadingOrders,
    createOrder,
    updateOrderStatus,
    deleteOrder,
    refreshOrders,
    refreshActiveOrders,

    // Order Limits
    orderLimits,
    loadingOrderLimits,
    refreshOrderLimits,

    // Tables
    tables,
    loadingTables,
    createTable,
    updateTable,
    deleteTable,
    refreshTables,

    // Utility
    clearAllData,
    loadPublicData,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}; 